import { useCallback, useEffect, useState, RefObject } from "react";

type DragInfos = "startX" | "startY" | "top" | "left" | "width" | "height";

type DragInfo = { [k in DragInfos]: number } | null;

type Key = "x" | "y";
type FinalPosition = Record<Key, number | string | undefined>;

interface UseDragProps {
  ref: RefObject<HTMLElement>;
  calculateFor?: "topLeft" | "bottomRight";
  initialPosition?: { x: number; y: number };
}

export const useDrag = ({
  ref,
  initialPosition = { x: 0, y: 0 },
  calculateFor = "topLeft",
}: UseDragProps) => {
  const [dragInfo, setDragInfo] = useState<DragInfo>(null);
  const [finalPosition, setFinalPosition] = useState<FinalPosition>({
    x: initialPosition.x,
    y: initialPosition.y,
  });
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const updateFinalPosition = useCallback(
    (width: number, height: number, x: number, y: number) => {
      if (calculateFor === "bottomRight") {
        setFinalPosition({
          x: Math.max(
            Math.min(
              window.innerWidth - width,
              window.innerWidth - (x + width)
            ),
            0
          ),
          y: Math.max(
            Math.min(
              window.innerHeight - height,
              window.innerHeight - (y + height)
            ),
            0
          ),
        });
      } else {
        setFinalPosition({
          x: Math.min(Math.max(0, x), window.innerWidth - width),
          y: Math.min(Math.max(0, y), window.innerHeight - height),
        });
      }
    },
    [calculateFor]
  );

  const handleMouseUp = useCallback((evt: MouseEvent) => {
    evt.preventDefault();
    setIsDragging(false);
  }, []);

  const handleMouseDown = (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();
    const { clientX, clientY } = evt;
    const draggableElement = ref.current;

    if (!draggableElement) return;

    const { top, left, width, height } =
      draggableElement.getBoundingClientRect();
    setIsDragging(true);
    setDragInfo({
      startX: clientX,
      startY: clientY,
      top,
      left,
      width,
      height,
    });
  };

  const handleMouseMove = useCallback(
    (evt: MouseEvent) => {
      if (!isDragging || !dragInfo) return;

      evt.preventDefault();
      const { clientX, clientY } = evt;
      const position = {
        x: dragInfo.startX - clientX,
        y: dragInfo.startY - clientY,
      };
      updateFinalPosition(
        dragInfo.width,
        dragInfo.height,
        dragInfo.left - position.x,
        dragInfo.top - position.y
      );
    },
    [isDragging, dragInfo, updateFinalPosition]
  );

  useEffect(() => {
    // Cast the event listener type to resolve TypeScript type issues
    const mouseMoveListener = handleMouseMove as unknown as EventListener;
    const mouseUpListener = handleMouseUp as unknown as EventListener;

    document.addEventListener("mousemove", mouseMoveListener);
    document.addEventListener("mouseup", mouseUpListener);

    return () => {
      document.removeEventListener("mousemove", mouseMoveListener);
      document.removeEventListener("mouseup", mouseUpListener);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    position: finalPosition,
    handleMouseDown,
  };
};
